/**
 * Deterministic pricing utility for Fox River AI
 * This module provides consistent pricing calculations without LLM variability
 */

/**
 * Extract project features from user input using regex patterns instead of LLM
 * @param {String} input - User input text
 * @returns {Object} - Extracted features
 */
export const extractProjectFeatures = (input) => {
  const lowerInput = input.toLowerCase();
  
  // Initialize features object
  const features = {
    projectType: null,
    skuCount: null,
    productCount: null,
    warehouseCount: null,
    documentCount: null,
    needsMemory: false,
    needsTools: false,
    needsOrchestration: false,
    hasIoTIntegration: false,
    hasRealtimeRequirement: false,
    hasComplianceRequirements: false,
    isEnterpriseScale: false,
    integrationCount: null,
    specializedUseCase: null,
    dataSize: null
  };
  
  // Project type detection with regex patterns
  if (/(inventory|stock|warehouse|sku|product).*(management|system|track)/i.test(lowerInput)) {
    features.projectType = 'inventory';
  } else if (/(document|contract|pdf|extract).*(process|analys|ai)/i.test(lowerInput)) {
    features.projectType = 'document_ai';
  } else if (/(image|vision|object detection|recognition)/i.test(lowerInput)) {
    features.projectType = 'computer_vision';
  } else if (/(agent|assistant|autonomous|multi-step|task planning)/i.test(lowerInput)) {
    features.projectType = 'genai_agent';
  } else if (/(chatbot|chat|conversation).*(ai|assistant|help)/i.test(lowerInput)) {
    features.projectType = 'chatbot';
  } else if (/(data|analytics|insight|dashboard)/i.test(lowerInput)) {
    features.projectType = 'analytics';
  } else if (/(recommend|suggestion|personalize)/i.test(lowerInput)) {
    features.projectType = 'recommendation';
  }
  
  // Extract numeric values with regex
  const skuMatch = /(\\d{1,3}(,\\d{3})*|\\d+)\\s*(skus|products|items)/i.exec(lowerInput);
  if (skuMatch) {
    features.skuCount = parseInt(skuMatch[1].replace(/,/g, ''));
  }
  
  const warehouseMatch = /(\\d+)\\s*(warehouses|locations|facilities)/i.exec(lowerInput);
  if (warehouseMatch) {
    features.warehouseCount = parseInt(warehouseMatch[1]);
  }
  
  const documentMatch = /(\\d{1,3}(,\\d{3})*|\\d+)\\s*(documents|files|pdfs)/i.exec(lowerInput);
  if (documentMatch) {
    features.documentCount = parseInt(documentMatch[1].replace(/,/g, ''));
  }
  
  const integrationMatch = /(\\d+)\\s*(integrations|systems|apis)/i.exec(lowerInput);
  if (integrationMatch) {
    features.integrationCount = parseInt(integrationMatch[1]);
  }
  
  // Data size extraction
  const dataSizeMatch = /(\\d+)\\s*(gb|tb|mb)/i.exec(lowerInput);
  if (dataSizeMatch) {
    features.dataSize = {
      size: parseInt(dataSizeMatch[1]),
      unit: dataSizeMatch[2].toUpperCase()
    };
  }
  
  // Feature detection with regex
  features.needsMemory = /(memory|remember|history|context)/i.test(lowerInput);
  features.needsTools = /(tools|integration|connect|api)/i.test(lowerInput);
  features.needsOrchestration = /(orchestration|workflow|multi-step|complex process)/i.test(lowerInput);
  features.hasIoTIntegration = /(iot|internet of things|sensors|devices)/i.test(lowerInput);
  features.hasRealtimeRequirement = /(real-time|realtime|live|streaming)/i.test(lowerInput);
  features.hasComplianceRequirements = /(compliance|hipaa|gdpr|regulatory|secure)/i.test(lowerInput);
  features.isEnterpriseScale = /(enterprise|large scale|corporate|company-wide)/i.test(lowerInput);
  
  // Specialized use case detection
  if (/(legal|contract|law|attorney)/i.test(lowerInput)) {
    features.specializedUseCase = 'legal_nlp';
  } else if (/(medical|health|patient|clinical)/i.test(lowerInput)) {
    features.specializedUseCase = 'medical_nlp';
  } else if (/(financial|finance|banking|investment)/i.test(lowerInput)) {
    features.specializedUseCase = 'financial_nlp';
  } else if (/(medical imaging|x-ray|radiology|scan)/i.test(lowerInput)) {
    features.specializedUseCase = 'medical_imaging';
  }
  
  return features;
};

/**
 * Determine project complexity based on extracted features
 * @param {Object} features - Extracted project features
 * @returns {String} - Complexity level: 'basic', 'standard', or 'complex'
 */
export const determineComplexity = (features) => {
  let complexityScore = 0;
  
  // Add points for basic features
  if (features.needsMemory) complexityScore += 1;
  if (features.needsTools) complexityScore += 1;
  if (features.needsOrchestration) complexityScore += 2;
  
  // Add points for advanced features
  if (features.hasIoTIntegration) complexityScore += 2;
  if (features.hasRealtimeRequirement) complexityScore += 1;
  if (features.hasComplianceRequirements) complexityScore += 2;
  if (features.isEnterpriseScale) complexityScore += 2;
  
  // Add points for multiple integrations
  if (features.integrationCount && features.integrationCount > 1) {
    complexityScore += Math.min(3, features.integrationCount - 1);
  }
  
  // Add points for specialized use cases
  if (features.specializedUseCase === 'legal_nlp' || features.specializedUseCase === 'medical_nlp') {
    complexityScore += 2;
  } else if (features.specializedUseCase === 'financial_nlp' || features.specializedUseCase === 'medical_imaging') {
    complexityScore += 1;
  }
  
  // Add points for data volume
  if (features.documentCount) {
    if (features.documentCount > 3000) complexityScore += 2;
    else if (features.documentCount > 500) complexityScore += 1;
  }
  
  if (features.skuCount) {
    if (features.skuCount > 3000) complexityScore += 2;
    else if (features.skuCount > 500) complexityScore += 1;
  }
  
  if (features.warehouseCount && features.warehouseCount > 1) {
    if (features.warehouseCount > 3) complexityScore += 2;
    else complexityScore += 1;
  }
  
  // Add points for data size
  if (features.dataSize) {
    if (features.dataSize.unit === 'TB' && features.dataSize.size > 1) complexityScore += 2;
    else if (features.dataSize.unit === 'GB' && features.dataSize.size > 500) complexityScore += 2;
    else if (features.dataSize.unit === 'GB' && features.dataSize.size > 100) complexityScore += 1;
  }
  
  // Determine complexity based on score
  if (complexityScore <= 1) {
    return 'basic';
  } else if (complexityScore <= 4) {
    return 'standard';
  } else {
    return 'complex';
  }
};

/**
 * Get base costs for a project type and complexity
 * @param {String} projectType - Type of project
 * @param {String} complexity - Project complexity
 * @returns {Object} - Base costs
 */
export const getBaseCosts = (projectType, complexity) => {
  const baseCosts = {
    inventory: {
      basic: { dev: 15000, monthly: 1000 },
      standard: { dev: 30000, monthly: 2000 },
      complex: { dev: 60000, monthly: 4000 }
    },
    document_ai: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    },
    computer_vision: {
      basic: { dev: 25000, monthly: 2000 },
      standard: { dev: 50000, monthly: 4000 },
      complex: { dev: 100000, monthly: 8000 }
    },
    genai_agent: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    },
    chatbot: {
      basic: { dev: 15000, monthly: 1000 },
      standard: { dev: 30000, monthly: 2000 },
      complex: { dev: 60000, monthly: 4000 }
    },
    analytics: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    },
    recommendation: {
      basic: { dev: 15000, monthly: 1000 },
      standard: { dev: 30000, monthly: 2000 },
      complex: { dev: 60000, monthly: 4000 }
    },
    // Default for any other project type
    default: {
      basic: { dev: 20000, monthly: 1500 },
      standard: { dev: 40000, monthly: 3000 },
      complex: { dev: 80000, monthly: 6000 }
    }
  };
  
  return (baseCosts[projectType] || baseCosts.default)[complexity];
};

/**
 * Calculate project costs deterministically
 * @param {Object} features - Project features
 * @returns {Object} - Cost breakdown
 */
export const calculateDeterministicCosts = (features) => {
  const projectType = features.projectType || 'default';
  const complexity = determineComplexity(features);
  
  // Get base costs
  const costs = getBaseCosts(projectType, complexity);
  let baseDev = costs.dev;
  let baseMonthly = costs.monthly;
  
  // Apply feature-specific cost adjustments
  let costMultiplier = 1.0;
  
  // IoT integration is expensive
  if (features.hasIoTIntegration) {
    costMultiplier += 0.2;
    baseMonthly += 500;
  }
  
  // Real-time requirements add costs
  if (features.hasRealtimeRequirement) {
    costMultiplier += 0.15;
    baseMonthly += 300;
  }
  
  // Compliance requirements add costs
  if (features.hasComplianceRequirements) {
    costMultiplier += 0.15;
    baseMonthly += 400;
  }
  
  // Multiple integrations add costs
  if (features.integrationCount && features.integrationCount > 1) {
    costMultiplier += 0.1 * Math.min(3, features.integrationCount - 1);
    baseMonthly += 200 * Math.min(3, features.integrationCount - 1);
  }
  
  // Integration type affects costs
  if (features.integrationType && features.integrationType !== 'none') {
    // Different integration types have different complexity
    switch (features.integrationType) {
      case 'ERP':
        costMultiplier += 0.15;
        baseMonthly += 350;
        break;
      case 'CRM':
        costMultiplier += 0.1;
        baseMonthly += 250;
        break;
      case 'POS':
        costMultiplier += 0.12;
        baseMonthly += 300;
        break;
      case 'general':
        costMultiplier += 0.08;
        baseMonthly += 200;
        break;
      default:
        break;
    }
  }
  
  // Specialized use cases add costs
  if (features.specializedUseCase === 'legal_nlp' || features.specializedUseCase === 'medical_nlp') {
    costMultiplier += 0.2;
    baseMonthly += 500;
  } else if (features.specializedUseCase === 'financial_nlp' || features.specializedUseCase === 'medical_imaging') {
    costMultiplier += 0.1;
    baseMonthly += 300;
  }
  
  // Advanced agent features add costs
  if (features.needsMemory) costMultiplier += 0.05;
  if (features.needsTools) costMultiplier += 0.1;
  if (features.needsOrchestration) costMultiplier += 0.15;
  
  // Enterprise scale adds costs
  if (features.isEnterpriseScale) {
    costMultiplier += 0.2;
    baseMonthly += 1000;
  }
  
  // Apply the cost multiplier
  baseDev = Math.round(baseDev * costMultiplier);
  
  // Apply Self Service Build Wizard discount if enabled
  const wizardDiscount = features.useSelfServiceWizard ? 0.3 : 1.0; // 70% discount with wizard
  
  // Calculate final costs with wizard discount
  const devCost = Math.round(baseDev * wizardDiscount);
  
  // Infrastructure costs are not discounted
  const rawInfrastructureCost = Math.round(baseMonthly * 0.3); // 30% for infrastructure (no discount)
  
  // Calculate discounted portion of monthly cost (maintenance and model updates)
  const discountedMonthlyPortion = baseMonthly * 0.7; // 70% for maintenance and model updates
  // eslint-disable-next-line no-unused-vars
  const discountedMonthlyCost = Math.round(discountedMonthlyPortion * wizardDiscount);
  
  // Only maintenance and model updates get the wizard discount
  const maintenanceCost = Math.round(discountedMonthlyPortion * 0.7 * wizardDiscount); // 70% of the discounted portion
  const modelUpdatesCost = Math.round(discountedMonthlyPortion * 0.3 * wizardDiscount); // 30% of the discounted portion
  
  // Total monthly cost combines non-discounted infrastructure with discounted maintenance/updates
  const totalMonthlyCost = rawInfrastructureCost + maintenanceCost + modelUpdatesCost;
  
  // Calculate cost ranges (±15%)
  const devCostMin = Math.round(devCost * 0.85);
  const devCostMax = Math.round(devCost * 1.15);
  const monthlyCostMin = Math.round(totalMonthlyCost * 0.85);
  const monthlyCostMax = Math.round(totalMonthlyCost * 1.15);
  
  // Break down development costs into phases
  const discoveryDesignCost = Math.round(devCost * 0.25);
  const implementationCost = Math.round(devCost * 0.55);
  const testingDeploymentCost = Math.round(devCost * 0.20);
  
  return {
    complexity,
    totalDevCost: devCost,
    totalDevRange: { min: devCostMin, max: devCostMax },
    totalMonthly: totalMonthlyCost,
    totalMonthlyRange: { min: monthlyCostMin, max: monthlyCostMax },
    breakdown: {
      development: {
        discoveryDesign: { min: Math.round(discoveryDesignCost * 0.85), max: Math.round(discoveryDesignCost * 1.15) },
        implementation: { min: Math.round(implementationCost * 0.85), max: Math.round(implementationCost * 1.15) },
        testingDeployment: { min: Math.round(testingDeploymentCost * 0.85), max: Math.round(testingDeploymentCost * 1.15) }
      },
      monthly: {
        infrastructure: { min: Math.round(rawInfrastructureCost * 0.85), max: Math.round(rawInfrastructureCost * 1.15) },
        maintenance: { min: Math.round(maintenanceCost * 0.85), max: Math.round(maintenanceCost * 1.15) },
        modelUpdates: { min: Math.round(modelUpdatesCost * 0.85), max: Math.round(modelUpdatesCost * 1.15) }
      }
    }
  };
};